import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import type { DisplayPreference } from '@/types/profile'

export default function PreferenceConfigPage() {
  const [preferences, setPreferences] = useState<DisplayPreference[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadPreferences()
  }, [])

  async function loadPreferences() {
    try {
      const { data, error } = await supabase
        .from('preference_display_config')
        .select('*')
        .order('category')
        .order('display_order')

      if (error) throw error
      setPreferences(data || [])
    } catch (error) {
      console.error('Failed to load preferences:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleVisibilityChange = async (
    category: string,
    preferenceKey: string,
    isVisible: boolean
  ) => {
    try {
      const { error } = await supabase
        .from('preference_display_config')
        .update({ is_visible: isVisible })
        .eq('category', category)
        .eq('preference_key', preferenceKey)

      if (error) throw error
      await loadPreferences()
    } catch (error) {
      console.error('Failed to update preference visibility:', error)
    }
  }

  const handleOrderChange = async (
    category: string,
    preferenceKey: string,
    direction: 'up' | 'down'
  ) => {
    const currentPref = preferences.find(
      p => p.category === category && p.preference_key === preferenceKey
    )
    if (!currentPref) return

    const sameCategoryPrefs = preferences.filter(p => p.category === category)
    const currentIndex = sameCategoryPrefs.findIndex(p => p.id === currentPref.id)
    
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === sameCategoryPrefs.length - 1)
    ) {
      return
    }

    const newOrder = direction === 'up'
      ? sameCategoryPrefs[currentIndex - 1].display_order
      : sameCategoryPrefs[currentIndex + 1].display_order

    try {
      const { error } = await supabase
        .from('preference_display_config')
        .update({ display_order: newOrder })
        .eq('id', currentPref.id)

      if (error) throw error
      await loadPreferences()
    } catch (error) {
      console.error('Failed to update preference order:', error)
    }
  }

  if (loading) {
    return <div>Loading preference configuration...</div>
  }

  const groupedPreferences = preferences.reduce((acc, pref) => {
    if (!acc[pref.category]) {
      acc[pref.category] = []
    }
    acc[pref.category].push(pref)
    return acc
  }, {} as Record<string, DisplayPreference[]>)

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-3xl font-semibold mb-6">
        Preference Display Configuration
      </h1>

      <div className="grid gap-6">
        {Object.entries(groupedPreferences).map(([category, prefs]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="capitalize">
                {category} Preferences
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {prefs.map((pref) => (
                  <div
                    key={pref.id}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="space-y-0.5">
                      <Label className="capitalize">
                        {pref.preference_key.replace('_', ' ')}
                      </Label>
                      <div className="text-sm text-muted-foreground">
                        Order: {pref.display_order}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOrderChange(
                            pref.category,
                            pref.preference_key,
                            'up'
                          )}
                        >
                          ↑
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOrderChange(
                            pref.category,
                            pref.preference_key,
                            'down'
                          )}
                        >
                          ↓
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={pref.is_visible}
                          onCheckedChange={(checked) => handleVisibilityChange(
                            pref.category,
                            pref.preference_key,
                            checked
                          )}
                        />
                        <Label>{pref.is_visible ? 'Visible' : 'Hidden'}</Label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}